import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { AuthorsList } from '../../articleComponents/author/AuthorsList';
import { H4, H1, semanticCSS } from '../../../typography/semanticTags';
import { BaseUnit } from '../../../theming/baseUnitDefinition';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';

const componentConfigurationKey = 'articleTitle';

interface ArticleTitleThemeProps {
  titleColor: string;
  metaDataColor: string;
  baseUnit: BaseUnit;
}

interface ArticleTitleDataProps {
  title: string;
  by: string;
  authors: AuthorsList;
  updatedAtFormatted: string;
  children?: React.ReactElement;
}

const createStyle = ({ titleColor, metaDataColor, baseUnit }: ArticleTitleThemeProps) => StyleSheet.create({
  wrapper: {
    position: 'relative',
  },
  title: {
    color: titleColor,
    marginTop: `${baseUnit}px`,
    marginBottom: `${baseUnit * 0.5}px`,
  },
  metaData: {
    color: metaDataColor,
  },
  sidesPadding: {
    marginTop: `${baseUnit * 0.5}px`,
    marginLeft: 0,
    marginRight: 0,
    [MEDIA_BREAKPOINTS.small]: {
      marginLeft: `${baseUnit}px`,
      marginRight: `${baseUnit}px`,
    },
  },
});

const shouldDisplayAuthors = (authors: AuthorsList) => {
  return authors.length > 0;
};

export const ArticleTitle: React.FunctionComponent<ArticleTitleDataProps> = props => {
  const { title, by, updatedAtFormatted, children, authors } = props;
  const themeProps = useTheme<ArticleTitleThemeProps>(componentConfigurationKey);
  const { metaDataColor } = themeProps;
  const style = createStyle(themeProps);
  const hasAuthors = shouldDisplayAuthors(authors);
  const separator = hasAuthors ? ' | ' : '';

  return (
    <ErrorBoundary>
      <H1 styles={semanticCSS(style.title, style.sidesPadding)}>{title}</H1>
      <div className={css(style.wrapper)}>
        <H4 styles={semanticCSS(style.metaData, style.sidesPadding)}>
          {hasAuthors && <AuthorsList by={by} authors={authors} color={metaDataColor} />}
          {`${separator}${updatedAtFormatted}`}
        </H4>
        {children}
      </div>
    </ErrorBoundary>
  );
};
